import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowRight, Instagram, Facebook, House } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { LionWatermark } from "./LionWatermark";
import { BRAND } from "../../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIALS = [
  { icon: Instagram, href: BRAND.socials.instagram, label: "Instagram" },
  { icon: Facebook, href: BRAND.socials.facebook, label: "Facebook" },
  { icon: House, href: BRAND.socials.houzz, label: "Houzz" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      toast.success("Thank you! Your request has been received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white placeholder:text-neutral-500 outline-none transition-colors focus:border-gold";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden border-t border-[#c9a24d]/25 bg-[#0a0a0a] py-24 lg:py-32"
    >
      <div className="relative z-10 mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
            Get In Touch
          </span>
          <h2 className="font-hero mt-4 text-3xl font-semibold tracking-normal text-white md:text-4xl lg:text-5xl">
            Request a{" "}
            <span className="gold-gradient-text italic">consultation</span>.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-neutral-400">
            Tell us about your project and our Toronto-based team will get
            back to you with a free, no-obligation quote. Serving Toronto,
            Etobicoke, Scarborough, North York, Mississauga and Oakville.
          </p>

          <div className="mt-10 space-y-5">
            <a href={BRAND.phoneHref} data-testid="contact-phone" className="group flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold transition-colors group-hover:bg-gold group-hover:text-black">
                <Phone size={18} />
              </span>
              <span className="text-neutral-200 transition-colors group-hover:text-gold">{BRAND.phone}</span>
            </a>
            <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="group flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold transition-colors group-hover:bg-gold group-hover:text-black">
                <Mail size={18} />
              </span>
              <span className="break-all text-neutral-200 transition-colors group-hover:text-gold">{BRAND.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold">
                <MapPin size={18} />
              </span>
              <span className="text-neutral-200">{BRAND.serviceArea}</span>
            </div>
          </div>

          <div className="mt-10 flex gap-3">
            {SOCIALS.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`contact-social-${label.toLowerCase()}`}
                className="flex h-11 w-11 items-center justify-center border border-white/15 text-neutral-300 transition-all hover:border-gold hover:text-gold"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="relative self-start">
          <LionWatermark className="top-1/2 left-full ml-8 hidden h-64 -translate-y-1/2 lg:block xl:h-80" />
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="relative border border-white/10 bg-[#0a0a0a] p-8 lg:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <input data-testid="contact-input-name" className={inputCls} placeholder="Full Name *" value={form.name} onChange={update("name")} />
              <input data-testid="contact-input-email" type="email" className={inputCls} placeholder="Email Address *" value={form.email} onChange={update("email")} />
              <input data-testid="contact-input-phone" className={inputCls} placeholder="Phone Number" value={form.phone} onChange={update("phone")} />
              <input data-testid="contact-input-service" className={inputCls} placeholder="Project Type" value={form.service} onChange={update("service")} />
            </div>
            <textarea
              data-testid="contact-input-message"
              rows={5}
              className={`${inputCls} mt-5 resize-none`}
              placeholder="Tell us about your project *"
              value={form.message}
              onChange={update("message")}
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="group mt-6 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:bg-[#c5a059] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Request"}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
